import { ContentHeader } from "../../components";
import { Form, Button, Input, Row, Col, Table, Space, InputNumber } from 'antd';
import { DeleteOutlined, SaveOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useEffect, useState } from "react";
import Column from 'antd/es/table/Column';
import { layDsCoSo, luuDMCoSo, xoaDMCoSo } from "@app/services/taisan";
import ModalTextForm from "@app/components/modal/ModalTextForm";
import { toast } from "react-toastify";
import { validateMessages } from '../../helpers/common';
import { hideLoadingScreen, showLoadingScreen } from '../../store/reducers/loading';
import { useDispatch } from 'react-redux';
import { DMCoSo } from "../../models/taisan/TaiSan";
import { searchLength } from "../../constants";

const QuanLyDMCoSo = () => {
    const [data, setData] = useState<Array<DMCoSo>>([]);
    const [page, setPage] = useState(1);
    const [tuKhoa, setTuKhoa] = useState<string>("");
    const [reloadPage, setReloadPage] = useState<boolean>(true);
    const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
    const [isLoadingBtn, setIsLoadingBtn] = useState<boolean>(false);
    const [showModalChiTiet, setShowModalChiTiet] = useState<boolean>(false);
    const [idXoa, setIdXoa] = useState<number>(0);
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const setFormDetailModal = (value: DMCoSo | null) => {
        form.setFieldsValue({
            "id": value?.id || 0,
            "maCoSo": value?.maCoSo || "",
            "tenCoSo": value?.tenCoSo || "",
            "diaChi": value?.diaChi || "",
            "ghiChu": value?.ghiChu || "",
            "thuTuHienThi": value?.thuTuHienThi ?? 1
        });
        setShowModalChiTiet(true);
    };

    const layDuLieu = async () => {
        try {
            setIsLoadingTable(true);
            let rs = await layDsCoSo();
            setData(rs || []);
            setIsLoadingTable(false);
        } catch (error: any) {
            setIsLoadingTable(false);
            toast.error(error.message || "Lỗi tải danh sách cơ sở");
        }
    };

    const onDelete = async () => {
        try {
            setIsLoadingBtn(true);
            const res = await xoaDMCoSo(idXoa);
            if (res.trangThai === 1 || res.duLieu) {
                toast.success('Xoá cơ sở thành công!');
                const totalRecords = data.length - 1;
                let newPage = page;
                if (newPage > Math.ceil(totalRecords / searchLength)) {
                    newPage = Math.max(1, Math.ceil(totalRecords / searchLength));
                }
                setPage(newPage);
            } else {
                toast.error(res.thongBao || 'Xoá cơ sở không thành công!');
            }
        } catch (err: any) {
            toast.error('Đã xảy ra lỗi, xoá không thành công!');
        } finally {
            setIdXoa(0);
            setReloadPage(true);
            setIsLoadingBtn(false);
        }
    };

    const onFinish = async (values: any) => {
        try {
            dispatch(showLoadingScreen());
            setIsLoadingBtn(true);
            const res = await luuDMCoSo(values);
            if (res.trangThai === 1 || res.duLieu > 0) {
                setShowModalChiTiet(false);
                toast.success('Lưu thông tin cơ sở thành công!');
            } else {
                toast.error(res.thongBao || 'Lưu không thành công!');
            }
        } catch (err: any) {
            setShowModalChiTiet(false);
            toast.error('Đã xảy ra lỗi, lưu không thành công!');
        } finally {
            dispatch(hideLoadingScreen());
            setIsLoadingBtn(false);
            setReloadPage(true);
        }
    };

    useEffect(() => {
        if (reloadPage) {
            layDuLieu();
            setReloadPage(false);
        }
    }, [reloadPage]);

    return (
        <section>
            <ContentHeader title="Quản lý danh mục cơ sở kiểm kê tài sản" />
            {idXoa > 0 && (
                <ModalTextForm
                    show={idXoa > 0}
                    title="Xoá cơ sở"
                    handleClose={() => setIdXoa(0)}
                    handleSave={onDelete}
                >
                    Xác nhận xóa cơ sở đang chọn?
                </ModalTextForm>
            )}
            {showModalChiTiet && (
                <ModalTextForm
                    title="Thông tin danh mục cơ sở"
                    hideFooter
                    show={showModalChiTiet}
                    handleClose={() => setShowModalChiTiet(false)}
                >
                    <Form
                        layout="vertical"
                        validateMessages={validateMessages}
                        onFinish={onFinish}
                        form={form}
                    >
                        <Form.Item hidden name="id"><Input /></Form.Item>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Mã cơ sở"
                                    name="maCoSo"
                                    rules={[{ required: true, message: 'Vui lòng nhập mã cơ sở' }]}
                                >
                                    <Input placeholder="Ví dụ: CS1, CS2, VOHINH" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Thứ tự hiển thị"
                                    name="thuTuHienThi"
                                >
                                    <InputNumber style={{ width: "100%" }} min={0} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    label="Tên cơ sở"
                                    name="tenCoSo"
                                    rules={[{ required: true, message: 'Vui lòng nhập tên cơ sở' }]}
                                >
                                    <Input placeholder="Ví dụ: Cơ sở 1 - 25 Pasteur" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    label="Địa chỉ"
                                    name="diaChi"
                                >
                                    <Input placeholder="Địa chỉ chi tiết (nếu có)" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    label="Ghi chú"
                                    name="ghiChu"
                                >
                                    <Input.TextArea rows={2} placeholder="Ghi chú" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item>
                            <Button type="primary" loading={isLoadingBtn} className="float-right" htmlType="submit" icon={<SaveOutlined rev="" />}>
                                Lưu
                            </Button>
                        </Form.Item>
                    </Form>
                </ModalTextForm>
            )}
            <Row className="form-group" gutter={8}>
                <Col span={8}>
                    <Input
                        prefix={<SearchOutlined rev="" />}
                        placeholder="Nhập tên hoặc mã cơ sở để tìm kiếm"
                        onChange={(e) => { setPage(1); setTuKhoa(e.target.value); }}
                        allowClear
                    />
                </Col>
                <Col span={16}>
                    <Button
                        className="hrm-btn hrm-btn-warning float-right"
                        onClick={() => {
                            setFormDetailModal(null);
                        }}
                        icon={<PlusOutlined rev="" />}
                    >
                        Thêm mới
                    </Button>
                </Col>
            </Row>
            <Row gutter={8}>
                <Col span={24}>
                    <div className="card card-primary">
                        <Row>
                            <Col span={24}>
                                <Table
                                    loading={isLoadingTable}
                                    locale={{ emptyText: "Không có dữ liệu" }}
                                    dataSource={data
                                        .map((item: any, index) => { item.key = item.id || index; return item; })
                                        .filter((record: DMCoSo) =>
                                            (record.tenCoSo?.toLowerCase().includes(tuKhoa.toLowerCase()) ||
                                             record.maCoSo?.toLowerCase().includes(tuKhoa.toLowerCase()))
                                        )}
                                    rowClassName={(record) => {
                                        return (record?.id === idXoa) ? "selected-row row-key-" : "";
                                    }}
                                    pagination={{
                                        current: page,
                                        pageSize: searchLength,
                                        onChange: (p) => {
                                            setPage(p);
                                        },
                                    }}
                                >
                                    <Column
                                        width="60px"
                                        title="STT"
                                        key="STT"
                                        align="center"
                                        render={(value, record, index) => (
                                            <span key={"stt" + index}>
                                                {(page - 1) * searchLength + index + 1}
                                            </span>
                                        )}
                                    />
                                    <Column width="120px" title="Mã cơ sở" dataIndex="maCoSo" key="maCoSo" />
                                    <Column title="Tên cơ sở" dataIndex="tenCoSo" key="tenCoSo" />
                                    <Column title="Địa chỉ" dataIndex="diaChi" key="diaChi" />
                                    <Column width="90px" title="Thứ tự" dataIndex="thuTuHienThi" key="thuTuHienThi" align="center" />
                                    <Column
                                        width="150px"
                                        title="Thao tác"
                                        key="action"
                                        align="center"
                                        render={(value: DMCoSo) => (
                                            <Space>
                                                <Button
                                                    type="primary"
                                                    size={"small"}
                                                    title="Sửa"
                                                    icon={<EditOutlined rev="" />}
                                                    onClick={() => { setFormDetailModal(value); }}
                                                >
                                                    Sửa
                                                </Button>
                                                <Button
                                                    type="primary"
                                                    danger
                                                    size={"small"}
                                                    title="Xóa"
                                                    onClick={() => { setIdXoa(value.id); }}
                                                    icon={<DeleteOutlined rev="" />}
                                                >
                                                    Xóa
                                                </Button>
                                            </Space>
                                        )}
                                    />
                                </Table>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </section>
    );
};

export default QuanLyDMCoSo;